/**
 * SchoolRemindersView — per-kid "what to remember" School view.
 *
 * Replaces the Compass timetable + generic packing checklist in the School tab.
 * Surfaces Wear / Bring / Today reminders per child, defaults to Tomorrow
 * (evening "pack for tomorrow" use case). Today shows a retrospective with
 * missed items flagged.
 *
 * State: dayId toggled by the date-nav pill in Calendar.tsx (schoolDate prop).
 * Checked state is persisted to localStorage keyed by `${dateStr}:${reminderId}`.
 */

import { useMemo } from 'react';
import { format, isToday } from 'date-fns';
import {
  GraduationCap,
  Clock,
  Mail,
  Sparkles,
  Hand,
  AlertTriangle,
  Check,
} from 'lucide-react';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { trackEvent } from '../../lib/analytics';
import type { Member } from '../../types/calendar';
import type {
  KidDayReminder,
  ReminderSource,
} from '../../types/schoolReminders';
import { REMINDER_SECTIONS, SOURCE_META } from '../../types/schoolReminders';

// ---------------------------------------------------------------------------
// Helper: hex colour → rgba string
// ---------------------------------------------------------------------------
/**
 * Convert a 6-digit hex colour (#rrggbb) to rgba(r,g,b,alpha).
 * Member.color in this codebase is always a 6-digit hex (e.g. "#1184a7").
 * If for any reason an unexpected format is passed, falls back to a neutral grey.
 */
function hexA(hex: string, alpha: number): string {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) {
    return `rgba(148,167,181,${alpha})`; // --text-muted fallback
  }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// ---------------------------------------------------------------------------
// Source chip icon
// ---------------------------------------------------------------------------
function SourceIcon({ source, size = 12 }: { source: ReminderSource; size?: number }) {
  const props = { size, strokeWidth: 2 };
  switch (source) {
    case 'compass': return <GraduationCap {...props} />;
    case 'email':   return <Mail {...props} />;
    case 'rule':    return <Sparkles {...props} />;
    case 'manual':  return <Hand {...props} />;
  }
}

function SourceChip({ source }: { source: ReminderSource }) {
  const meta = SOURCE_META[source];
  return (
    <span
      title={meta.label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2px 5px',
        borderRadius: 6,
        color: meta.tone,
        background: hexA(meta.tone, 0.12),
        flexShrink: 0,
      }}
    >
      <SourceIcon source={source} size={12} />
    </span>
  );
}

// ---------------------------------------------------------------------------
// Progress ring
// ---------------------------------------------------------------------------
function ProgressRing({
  done,
  total,
  color,
  size = 42,
}: {
  done: number;
  total: number;
  color: string;
  size?: number;
}) {
  const strokeWidth = 3.5;
  const r = (size - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * r;
  const pct = total === 0 ? 0 : done / total;
  const allDone = total > 0 && done === total;

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ display: 'block' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={hexA(color, 0.18)}
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - pct)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 0.35s ease' }}
        />
      </svg>
      <span
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          color: allDone ? color : 'var(--text-secondary)',
        }}
      >
        {allDone ? <Check size={14} strokeWidth={3} color={color} /> : `${done}/${total}`}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reminder row
// ---------------------------------------------------------------------------
interface ReminderItemProps {
  item: KidDayReminder;
  kidColor: string;
  checked: boolean;
  onToggle: (id: string) => void;
}

function ReminderItem({ item, kidColor, checked, onToggle }: ReminderItemProps) {
  const flagged = item.parentAction || item.critical;
  const isMissed = item.missed === true;

  let borderColor = 'var(--border-light)';
  let background = '#ffffff';

  if (checked) {
    background = '#EEF4F7';
  } else if (isMissed) {
    borderColor = 'rgba(234,81,95,0.3)';
    background = 'rgba(234,81,95,0.05)';
  } else if (flagged) {
    borderColor = hexA(kidColor, 0.35);
    background = hexA(kidColor, 0.05);
  }

  return (
    <button
      onClick={() => onToggle(item.id)}
      data-checked={checked ? 'true' : 'false'}
      data-missed={isMissed ? 'true' : 'false'}
      data-flagged={flagged ? 'true' : 'false'}
      data-kid-color={kidColor}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '11px 13px',
        borderRadius: 12,
        border: `1px solid ${borderColor}`,
        background,
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'background 0.15s, transform 0.1s',
      }}
      onMouseEnter={(e) => {
        if (!checked) (e.currentTarget as HTMLButtonElement).style.background = '#F5F9FB';
      }}
      onMouseLeave={(e) => {
        // Re-derive background at handler time so we don't capture the stale
        // render-time value (checked prop may have changed since render).
        const el = e.currentTarget as HTMLButtonElement;
        const isCheckedNow = el.getAttribute('data-checked') === 'true';
        const isMissedNow = el.getAttribute('data-missed') === 'true';
        const isFlaggedNow = el.getAttribute('data-flagged') === 'true';
        const color = el.getAttribute('data-kid-color') ?? '#94a7b5';
        let bg = '#ffffff';
        if (isCheckedNow) {
          bg = '#EEF4F7';
        } else if (isMissedNow) {
          bg = 'rgba(234,81,95,0.05)';
        } else if (isFlaggedNow) {
          bg = hexA(color, 0.05);
        }
        el.style.background = bg;
      }}
      onMouseDown={(e) => {
        (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.992)';
      }}
      onMouseUp={(e) => {
        (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
      }}
    >
      {/* Checkbox */}
      <span
        style={{
          width: 26,
          height: 26,
          borderRadius: 7,
          border: checked ? `2px solid ${kidColor}` : '2px solid #E2E8F0',
          background: checked ? kidColor : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'background 0.15s, border-color 0.15s',
        }}
      >
        {checked && <Check size={14} strokeWidth={3} color="#ffffff" />}
      </span>

      {/* Emoji */}
      <span
        aria-hidden="true"
        style={{ fontSize: 21, width: 26, textAlign: 'center', flexShrink: 0 }}
      >
        {item.emoji}
      </span>

      {/* Main content */}
      <span style={{ flex: 1, minWidth: 0 }}>
        {/* Label row */}
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: 16.5,
              fontWeight: 600,
              color: checked ? 'var(--text-muted)' : '#0B3C7A',
              textDecoration: checked ? 'line-through' : 'none',
              filter: checked ? 'saturate(0)' : 'none',
            }}
          >
            {item.label}
          </span>
          {item.time && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
                fontSize: 12,
                fontWeight: 700,
                color: '#fe7434',
                background: 'rgba(254,116,52,0.12)',
                padding: '2px 7px',
                borderRadius: 6,
                whiteSpace: 'nowrap',
              }}
            >
              <Clock size={11} strokeWidth={2.25} />
              {item.time}
            </span>
          )}
          {item.day && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--text-secondary)',
                background: '#EEF4F7',
                padding: '2px 7px',
                borderRadius: 6,
                whiteSpace: 'nowrap',
              }}
            >
              {item.day}
            </span>
          )}
        </span>
        {/* Detail */}
        <span
          style={{
            display: 'block',
            fontSize: 12.5,
            color: 'var(--text-secondary)',
            marginTop: 1,
          }}
        >
          {item.detail}
        </span>
      </span>

      {/* Right: badge + source */}
      <span style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
        {isMissed ? (
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              color: '#ea515f',
              background: 'rgba(234,81,95,0.12)',
              padding: '2px 6px',
              borderRadius: 6,
            }}
          >
            Missed
          </span>
        ) : flagged ? (
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              color: kidColor,
              background: hexA(kidColor, 0.12),
              padding: '2px 6px',
              borderRadius: 6,
            }}
          >
            {item.critical ? "Don't miss" : 'Parent'}
          </span>
        ) : null}
        <SourceChip source={item.source} />
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Section header
// ---------------------------------------------------------------------------
function SectionHeader({ section }: { section: typeof REMINDER_SECTIONS[number] }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '14px 0 6px',
      }}
    >
      <span style={{ fontSize: 16 }}>{section.emoji}</span>
      <span
        style={{
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--text-primary)',
        }}
      >
        {section.label}
      </span>
      <span
        style={{
          fontSize: 11.5,
          color: 'var(--text-muted)',
          whiteSpace: 'nowrap',
        }}
      >
        {section.hint}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subjects strip
// ---------------------------------------------------------------------------
function SubjectStrip({
  weekday,
  subjects,
  flagged,
  kidColor,
  cardPad,
}: {
  weekday: string;
  subjects: string[];
  flagged: string[];
  kidColor: string;
  cardPad: number;
}) {
  if (!subjects.length) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: `11px ${cardPad}px`,
        borderBottom: '1px solid var(--border-light)',
        background: hexA(kidColor, 0.03),
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: 'var(--text-secondary)',
        }}
      >
        <GraduationCap size={13} strokeWidth={2.25} color={kidColor} />
        {weekday} · {subjects.length} classes
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {subjects.map((s) => {
          const isFlag = flagged.includes(s);
          return (
            <span
              key={s}
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: isFlag ? kidColor : 'var(--text-secondary)',
                background: isFlag ? hexA(kidColor, 0.12) : '#EEF4F7',
                padding: '3px 9px',
                borderRadius: 99,
                boxShadow: isFlag ? `inset 0 0 0 1px ${hexA(kidColor, 0.28)}` : 'none',
              }}
            >
              {s}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Avatar
// ---------------------------------------------------------------------------
function KidAvatar({ member, size = 46 }: { member: Member; size?: number }) {
  const radius = Math.round(size * 0.28);
  const fontSize = Math.round(size * 0.36);
  const initials = member.name.slice(0, 2);

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: member.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ffffff',
        fontWeight: 700,
        fontSize,
        flexShrink: 0,
        boxShadow: '0 1px 3px rgba(8,40,80,0.15)',
      }}
    >
      {initials}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Kid card
// ---------------------------------------------------------------------------
interface KidCardProps {
  member: Member;
  items: KidDayReminder[];
  subjects: string[];
  flaggedSubjects: string[];
  weekday: string;
  isChecked: (id: string) => boolean;
  onToggle: (id: string) => void;
  yearLabel?: string;
}

function KidCard({
  member,
  items,
  subjects,
  flaggedSubjects,
  weekday,
  isChecked,
  onToggle,
  yearLabel,
}: KidCardProps) {
  const cardPad = 18;
  const done = items.filter((i) => isChecked(i.id)).length;
  const total = items.length;
  const flaggedCount = items.filter((i) => (i.parentAction || i.critical) && !i.missed).length;

  const bySection = REMINDER_SECTIONS.map((sec) => ({
    sec,
    list: items.filter((i) => i.section === sec.key),
  })).filter((g) => g.list.length > 0);

  return (
    <section
      style={{
        background: '#ffffff',
        border: '1px solid #EDF2F7',
        borderRadius: 20,
        boxShadow: '0 1px 3px rgba(8,40,80,0.06), 0 8px 24px rgba(8,40,80,0.05)',
        borderTop: `4px solid ${member.color}`,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 13,
          padding: `${cardPad}px`,
          borderBottom: '1px solid var(--border-light)',
          background: hexA(member.color, 0.10),
        }}
      >
        <KidAvatar member={member} size={46} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: '-0.01em',
              color: '#0B3C7A',
              lineHeight: 1.2,
            }}
          >
            {member.name}
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--text-secondary)',
              marginTop: 1,
            }}
          >
            {yearLabel}
            {flaggedCount > 0 && (
              <span style={{ color: member.color }}>
                {yearLabel ? ' · ' : ''}{flaggedCount} to not miss
              </span>
            )}
          </div>
        </div>
        <ProgressRing done={done} total={total} color={member.color} size={42} />
      </header>

      {/* Subjects strip */}
      <SubjectStrip
        weekday={weekday}
        subjects={subjects}
        flagged={flaggedSubjects}
        kidColor={member.color}
        cardPad={cardPad}
      />

      {/* Body */}
      <div style={{ padding: `6px ${cardPad}px ${cardPad}px` }}>
        {total === 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              padding: '24px 0',
              color: 'var(--text-secondary)',
            }}
          >
            <span style={{ fontSize: 28 }}>🎉</span>
            <p style={{ fontSize: 14, margin: 0 }}>Nothing extra to remember</p>
          </div>
        ) : (
          bySection.map(({ sec, list }) => (
            <div key={sec.key}>
              <SectionHeader section={sec} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {list.map((item) => (
                  <ReminderItem
                    key={item.id}
                    item={item}
                    kidColor={member.color}
                    checked={isChecked(item.id)}
                    onToggle={onToggle}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Alert banner
// ---------------------------------------------------------------------------
interface BannerItem {
  item: KidDayReminder;
  member: Member;
}

function AlertBanner({
  dayId,
  items,
}: {
  dayId: 'today' | 'tomorrow';
  items: BannerItem[];
}) {
  if (dayId === 'today') {
    const missed = items.filter((x) => x.item.missed);
    if (!missed.length) return null;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          padding: '14px 16px',
          borderRadius: 12,
          background: 'rgba(234,81,95,0.07)',
          border: '1px solid rgba(234,81,95,0.25)',
          marginBottom: 20,
        }}
      >
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: 'rgba(234,81,95,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <AlertTriangle size={18} color="#ea515f" strokeWidth={2} />
        </div>
        <div style={{ fontSize: 14, color: '#0B3C7A', lineHeight: 1.5 }}>
          <strong>{missed.length} {missed.length === 1 ? 'thing' : 'things'} slipped through today.</strong>{' '}
          <span>
            {missed.map((m, k) => (
              <em key={k} style={{ color: m.member.color, fontStyle: 'normal' }}>
                {m.member.name}: {m.item.label}
                {k < missed.length - 1 ? ' · ' : ''}
              </em>
            ))}
            {' '}Worth a glance here each evening to catch anything missed.
          </span>
        </div>
      </div>
    );
  }

  // Tomorrow banner — flagged/critical/parentAction items
  const flagged = items.filter((x) => (x.item.parentAction || x.item.critical) && !x.item.missed);
  if (!flagged.length) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '14px 16px',
        borderRadius: 12,
        background: 'rgba(21,162,162,0.07)',
        border: '1px solid rgba(21,162,162,0.22)',
        marginBottom: 20,
      }}
    >
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: 10,
          background: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: '0 1px 3px rgba(8,40,80,0.1)',
        }}
      >
        <Sparkles size={18} color="#15a2a2" strokeWidth={2} />
      </div>
      <div style={{ fontSize: 14, color: '#0B3C7A', lineHeight: 1.5 }}>
        <strong>Don't miss tomorrow:</strong>{' '}
        <span>
          {flagged.map((f, k) => (
            <em key={k} style={{ color: f.member.color, fontStyle: 'normal' }}>
              {f.member.name} — {f.item.label}
              {f.item.time ? ` ${f.item.time}` : ''}
              {k < flagged.length - 1 ? ' · ' : ''}
            </em>
          ))}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
export interface SchoolRemindersViewProps {
  /** The date being displayed (drives Today vs Tomorrow label) */
  date: Date;
  /** Child/teen members to show (adults are filtered out by caller) */
  members: Member[];
  /**
   * Reminders for this date, keyed by memberId.
   * Caller fetches/derives these from the calendar event store.
   */
  remindersByMember: Record<string, KidDayReminder[]>;
  /**
   * Subjects from Compass for this date, keyed by memberId.
   */
  subjectsByMember?: Record<string, string[]>;
  /**
   * Subjects to emphasise (tied to a reminder), keyed by memberId.
   */
  flaggedSubjectsByMember?: Record<string, string[]>;
  /**
   * Optional year label per member, e.g. "Year 9".
   * Keyed by memberId.
   */
  yearLabelByMember?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------
export function SchoolRemindersView({
  date,
  members,
  remindersByMember,
  subjectsByMember = {},
  flaggedSubjectsByMember = {},
  yearLabelByMember = {},
}: SchoolRemindersViewProps) {
  const dayId: 'today' | 'tomorrow' = isToday(date) ? 'today' : 'tomorrow';
  const dateStr = format(date, 'yyyy-MM-dd');
  const weekday = format(date, 'EEEE');

  // Checked state: keyed by `${dateStr}:${reminderId}` so ticks are scoped to
  // the real calendar date, not just 'today'/'tomorrow' which would collide
  // across calendar dates (e.g. two different Mondays both resolve to 'tomorrow').
  const [checked, setChecked] = useLocalStorage<Record<string, boolean>>(
    'school-reminders-checked',
    {},
  );

  const isChecked = (id: string): boolean => !!checked[`${dateStr}:${id}`];

  const handleToggle = (member: Member, id: string) => {
    const key = `${dateStr}:${id}`;
    const wasChecked = !!checked[key];
    setChecked((prev) => ({ ...prev, [key]: !wasChecked }));
    trackEvent('school_reminder_toggled', {
      day_id: dayId,
      date_str: dateStr,
      reminder_id: id,
      member_id: member.id,
      checked: !wasChecked,
    });
  };

  // Flatten all items for the alert banner
  const allBannerItems = useMemo<BannerItem[]>(() => {
    const out: BannerItem[] = [];
    for (const member of members) {
      const items = remindersByMember[member.id] ?? [];
      for (const item of items) {
        out.push({ item, member });
      }
    }
    return out;
  }, [members, remindersByMember]);

  return (
    <div
      style={{
        padding: '20px 22px 32px',
        overflowY: 'auto',
        height: '100%',
        boxSizing: 'border-box',
      }}
    >
      <AlertBanner dayId={dayId} items={allBannerItems} />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
          gap: 20,
          alignItems: 'start',
        }}
      >
        {members.map((member) => {
          const items = remindersByMember[member.id] ?? [];
          const subjects = subjectsByMember[member.id] ?? [];
          const flaggedSubjects = flaggedSubjectsByMember[member.id] ?? [];

          return (
            <KidCard
              key={member.id}
              member={member}
              items={items}
              subjects={subjects}
              flaggedSubjects={flaggedSubjects}
              weekday={weekday}
              isChecked={isChecked}
              onToggle={(id) => handleToggle(member, id)}
              yearLabel={yearLabelByMember[member.id]}
            />
          );
        })}
      </div>
    </div>
  );
}
